import React from "react";
import styled, { css } from "styled-components";
import * as ReactDOM from "react-dom";

import { useEventListener } from "src/library/hooks";

import IconDropdownOption from "./IconDropdownOption";

interface IconDropdownProps {
  /** Array of items to render into dropdown options */
  items: Array<{
    label: string;
    value: string;
    icon: React.ReactElement;
  }>;

  /** callback to trigger when a dropdown item is selected */
  onSelect: (key: string) => void;

  /** References the key of the currently selected item, controls which option is displayed in closed state */
  currentSelection?: string;
}

/**
 * Show the currently selected dropdown option
 */
const IconDropdown: React.FC<IconDropdownProps> = ({
  items, onSelect, currentSelection
}) => {
  const [ isOpen, setIsOpen ] = React.useState(false);

  /** Stores the x and y coordinates for where the dropdown should render
   * from inside the dom when positioned absolute
   */
  const [ position, setPosition ] = React.useState({
    x: 0,
    y: 0
  });

  const currentItem = items.find(item => item.value === currentSelection);
  /** Begin positioning code */
  const popoutRef = React.useRef<HTMLDivElement>(null);
  const divRef = React.useRef<HTMLDivElement>(null);

  /** Closes the dropwdown when you click outside of the dropdown wrapper  */
  const outsideClickHandler = React.useCallback(
    (event: MouseEvent) => {
      if (popoutRef.current && popoutRef.current.contains && !popoutRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    },
    [ popoutRef ]
  );

  /**
   * Uses the div ref to figure out the x and y coordinates of the dropdown parent
   * and returns the x and y position that the dropdown should be rendered
   */
  const setOverlayPosition = React.useCallback(() => {
    if (divRef.current && divRef.current) {
      const bounds = divRef.current.getBoundingClientRect();
      let right = bounds.right - bounds.width;
      const inner = divRef.current.getBoundingClientRect();

      if (inner.width > window.outerWidth) {
        right = 0;
      } else if (inner.width > bounds.right) {
        right = 0;
      } else {
        right = bounds.right - inner.width;
      }

      setPosition({
        y: bounds.top + bounds.height,
        x: right
      });
    }
  }, [ divRef ]);

  /** Set position on open */
  React.useEffect(() => {
    if (isOpen) {
      setOverlayPosition();
    }
  }, [ isOpen, setOverlayPosition ]);
  useEventListener("mousedown", outsideClickHandler, document);
  // set poistion on resize & scroll
  useEventListener("resize", setOverlayPosition);
  useEventListener("scroll", setOverlayPosition, document.getElementById("main-wrapper"));

  return (
    <div ref={divRef}>
      <div className='dropdown-select'>
        <IconDropdownOption
          label={currentItem?.label || "-"}
          icon={currentItem?.icon}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        />
      </div>

      {ReactDOM.createPortal(
        <>
          {isOpen && (
            <StyledDropdownOptions
              {...position}
              className='icon-dropdown-options'
              ref={popoutRef}
            >
              {items.map((item, key) => (
                <IconDropdownOption
                  label={item.label}
                  icon={item.icon}
                  key={key}
                  onClick={() => {
                    onSelect(item.value);
                    setIsOpen(false);
                  }}
                />
              ))}
            </StyledDropdownOptions>
          )}
        </>
        , document.body)}
    </div>
  );
};

const StyledDropdownOptions = styled.div<{ x?: number; y?: number; }>`
  ${({
    y, x, theme
  }) => css`
    position: absolute;
    top: ${y}px;
    left: ${x}px;
    z-index: 10;
    text-align: left;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius};
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.2);
    overflow: hidden;
  `}
`;

export default IconDropdown;
